import { idb } from "../../db/index.ts";
import { g, helpers } from "../../util/index.ts";
import makeItWork from "./makeItWork.ts";
import summary from "./summary.ts";
import get from "./get.ts";
import { ValueChangeCalculator } from "../team/ValueChangeCalculator.ts";

/**
 * Make a trade work
 *
 * This should be called for a trade negotiation, as it will update the trade objectStore.
 *
 * @memberOf core.trade
 * @return {Promise.string} Resolves to a string containing a message to be dispalyed to the user, as if it came from the AI GM.
 */
const makeItWorkTrade = async () => {
	const tr = await get();
	const teams0 = tr.teams;
	const valueChangeCalculator = new ValueChangeCalculator();
	// At most 4 assets added, like the Trading Block: an unlimited search kept adding pieces on both sides and turned a
	// 4-for-1 offer into a 21-for-25 swap
	const found = await makeItWork(helpers.deepCopy(teams0), {
		holdUserConstant: false,
		maxAssetsToAdd: 4,
		valueChangeCalculator,
	});
	// The counteroffer only adds: everything the user asked for and offered stays in the trade
	const keepsEverything = (t: typeof found) =>
		t !== undefined &&
		([0, 1] as const).every(
			(i) =>
				teams0[i].pids.every((pid) => t[i].pids.includes(pid)) &&
				teams0[i].dpids.every((dpid) => t[i].dpids.includes(dpid)),
		);
	const teams = keepsEverything(found) ? found : undefined;

	if (!teams) {
		// Say which requested players made it impossible, instead of silently dropping them
		const requested: string[] = [];
		for (const pid of teams0[1].pids) {
			const p = await idb.cache.players.get(pid);
			if (p) {
				requested.push(`${p.firstName} ${p.lastName}`);
			}
		}
		return {
			changed: false,
			message: `${g.get("teamInfoCache")[teams0[1].tid]?.region} GM: "${
				requested.length > 0
					? `I can't find a deal that includes ${requested.join(", ")}.`
					: "I can't afford to give up so much."
			}"`,
		};
	}

	const s = await summary(teams); // Store AI's proposed trade in database, if it's different

	let updated = false;

	for (const i of [0, 1] as const) {
		if (teams[i].tid !== teams0[i].tid) {
			updated = true;
			break;
		}

		if (teams[i].pids.toString() !== teams0[i].pids.toString()) {
			updated = true;
			break;
		}

		if (teams[i].dpids.toString() !== teams0[i].dpids.toString()) {
			updated = true;
			break;
		}
	}

	if (updated) {
		const tr2 = await get();
		tr2.teams = teams;
		await idb.cache.trade.put(tr2);
	}

	if (s.warning) {
		return {
			changed: updated,
			message: `${
				g.get("teamInfoCache")[teams[1].tid]?.region
			} GM: "Something like this would work if you can figure out how to get it done without breaking the salary cap rules."`,
		};
	}

	return {
		changed: updated,
		message: `${
			g.get("teamInfoCache")[teams[1].tid]?.region
		} GM: "How does this sound?"`,
	};
};

export default makeItWorkTrade;
